import React, { useState, useEffect } from 'react';
import NumberFormat from 'react-number-format';
import { Controller, useForm } from 'react-hook-form';

import { Checkbox } from 'primereact/checkbox';
import { Button } from 'primereact/button';
import { Dropdown } from 'primereact/dropdown';
import { InputText } from 'primereact/inputtext';
import { Tooltip } from 'primereact/tooltip';
import { InputSwitch } from 'primereact/inputswitch';

import { businessTypes, titles, types } from 'constants/dropdownOptions';
import { useRelationRole } from 'hooks/useRelationRole';
import { useBusinessRole } from 'hooks/useBusinessRole';
import { validatePhoneNumber } from 'modules/lead/helpers/helpers';
import { emailDuplicateCheck } from 'services/Contact/contactServices';
import { useUserDetailsContext } from 'context/userDetailsContext';
import { useCaseContact } from 'modules/cases/hooks/useCaseContact';
import { useCaseDetailsCustomField } from 'modules/cases/hooks/useCaseDetailsCustomField';
import { getContactTypeLabel } from 'utils/utils';
import CustomFields from 'modules/lead/AddLead/components/ContactForm/CustomFields';
import TextSnippetWrapper from 'components/TextSnippetWrapper';
import DuplicateDetection from './DuplicateDetection';
import { useExperts } from 'hooks/useExperts';
import GlobalTooltip from 'components/GlobalTooltip';
import { contactTextMessageLabel } from 'constants/contactConstants';
import { useCustomFieldsData } from '../../../settings/CustomField/hooks/useCustomFieldsData';
import InsuranceProviderFields from './components/InsuranceProviderFields';

export default function AddContact({
  isLeadSpecific,
  toggleAddContact,
  onContactSubmit,
  toggleAddBusiness,
  toggleAddLocation,
  toggle,
  practice_area = '',
}) {
  const {
    watch,
    formState: { errors },
    control,
    setValue,
    trigger,
    reset,
    handleSubmit,
    getValues,
  } = useForm({
    defaultValues: {
      contact_type: toggle === 'person' ? 'person' : toggle === 'business' ? 'business' : 'location',
    },
  });
  const [selectedContact, setSelectedContact] = useState('');

  const { checkSubPermission } = useUserDetailsContext();
  const { caseCustomFieldList } = useCaseDetailsCustomField();
  const { updateContactHandler } = useCaseContact({});

  const { customFields } = useCustomFieldsData();
  // useEffect(() => {
  //   setValue('custom_field', caseCustomFieldList);
  // }, [caseCustomFieldList]);

  useEffect(() => {
    if (toggle === 'person') {
      setValue('custom_field', customFields.contact);
    } else if (toggle === 'business') {
      setValue('custom_field', customFields.companies);
    }
  }, [customFields.contact, customFields.companies]);

  const contactType = toggle ? (toggle === 'person' ? 'person' : toggle === 'business' ? 'business' : 'location') : watch('contact_type');
  const businessType = watch('business_type');

  const isContactSelected = typeof selectedContact === 'object';

  const { relationRoleList } = useRelationRole(practice_area);
  const { businessRoleList } = useBusinessRole(practice_area);
  const { expertList } = useExperts(practice_area);

  const updatedRelationList = relationRoleList.filter((option) => option?.relation_role !== 'Client');

  const getFormErrorMessage = (name) => {
    return errors?.[name] && <small className="p-error">{errors[name].message}</small>;
  };

  const validatePhone = async (value) => {
    if (isContactSelected) return true;
    if (!Boolean(value)) {
      return true;
    }
    return validatePhoneNumber(value, null);
  };

  const emailValidation = async (value) => {
    if (isContactSelected) return true;
    if (!Boolean(value)) {
      return true;
    }
    try {
      await emailDuplicateCheck(value);
      return true;
    } catch (error) {
      return 'Email already exists';
    }
  };

  const onContactSelect = (contact) => {
    setSelectedContact(contact);
    let updatedContact = {
      // contact_type: contact.contact_type ?? '',
      title: contact.title ?? '',
      first_name: contact.first_name ?? '',
      last_name: contact.last_name ?? '',
      middle_name: contact.middle_name ?? '',
      email: contact.email ?? '',
      phone: contact.phone ?? '',
      role: contact.role ?? null,
      is_primary_contact: contact.is_primary_contact ?? '',
      is_billing_contact: contact.is_billing_contact ?? '',
      is_create_client_portal: contact.is_create_client_portal ?? '',
      custom_field: contact.custom_field ?? [],
      fax_number: contact.fax_number ?? '',
      expert_type: contact.expert_type ?? '',
      business_type: contact.business_type ?? null,
    };
    typeof contact === 'object' && reset({ ...contact, ...updatedContact });
  };

  const onDismiss = () => {
    reset({
      contact_type: toggle,
      title: '',
      first_name: '',
      last_name: '',
      middle_name: '',
      email: '',
      phone: '',
      role: null,
      is_primary_contact: null,
      is_billing_contact: null,
      is_create_client_portal: null,
      notify_case_updates: null,
      custom_field: caseCustomFieldList,
      fax_number: '',
      expert_type: null,
    });
    setSelectedContact('');
  };

  return (
    <React.Fragment>
      <DuplicateDetection
        value={selectedContact}
        onSelect={onContactSelect}
        onDismiss={onDismiss}
        toggle={toggle}
        isLeadSpecific={isLeadSpecific}
      />
      <div className="relations-box">
        <div className="d-flex flex-wrap mt-3 pt-3">
          <div className="col-12 col-lg-6 px-2 mb-3">
            <label className="p-medium" htmlFor="name">
              Contact Type
              <span className="text-danger"> *</span>
            </label>
            <Controller
              name="contact_type"
              control={control}
              rules={{ required: 'Required' }}
              render={({ field }) => (
                <Dropdown
                  className="input-shadow w-100"
                  placeholder="Contact Type"
                  options={toggle ? types : types?.filter((v) => v.value !== 'location')}
                  id={field.name}
                  value={field.value}
                  onChange={(e) => {
                    field.onChange(e.value);
                  }}
                  disabled={toggle || isContactSelected}
                />
              )}
            />
            {getFormErrorMessage('contact_type')}
          </div>
          {contactType && contactType === 'location' && (
            <div className="col-12 col-lg-6 px-2 mb-3">
              <label className="p-medium" htmlFor="name">
                Name
                <span className="text-danger"> *</span>
              </label>
              <Controller
                name="title"
                control={control}
                rules={{
                  required: 'Required',
                  pattern: {
                    value: /^[aA-zZ0-9\s'-]+$/,
                    message: 'Only letters, apostrophes, hyphens, or underscores are allowed',
                  },
                }}
                render={({ field }) => (
                  <TextSnippetWrapper
                    type="text"
                    disabled={isContactSelected}
                    className="input-shadow w-100"
                    placeholder="Name"
                    {...field}
                  />
                )}
              />
              {getFormErrorMessage('title')}
            </div>
          )}
          {contactType && contactType === 'business' && (
            <div className="col-12 col-lg-6 px-2 mb-3">
              <label className="p-medium" htmlFor="Business_Type">
                Business Type
              </label>
              <Controller
                name="business_type"
                control={control}
                render={({ field: { onChange, value } }) => (
                  <Dropdown
                    value={value}
                    onChange={(e) => onChange(e.value)}
                    name="business_type"
                    options={businessTypes}
                    placeholder="Select"
                    className="w-100"
                    inputStyle={{ width: '100%' }}
                    filter
                    showClear
                  />
                )}
              />
              {getFormErrorMessage('business_type')}
            </div>
          )}
          {contactType === 'business' && businessType === 'expert' && (
            <div className="col-12 col-lg-6 px-2 mb-3">
              <div className="col-12 mt-1">
                <label className="p-medium" htmlFor="Area_of_Expertise">
                  Expert Type
                  <span className="text-danger"> *</span>
                </label>
                <Controller
                  name="expert_type"
                  control={control}
                  rules={{ required: 'Required' }}
                  render={({ field: { onChange, value } }) => (
                    <Dropdown
                      value={value}
                      onChange={(e) => onChange(e.value)}
                      name="expert_type"
                      options={expertList}
                      optionLabel="title"
                      optionValue="expert_id"
                      placeholder="Select"
                      className="w-100"
                      inputStyle={{ width: '100%' }}
                      filter
                    />
                  )}
                />
                {getFormErrorMessage('expert_type')}
              </div>
            </div>
          )}
          {/* title field business name for business  */}

          {contactType && contactType === 'person' && (
            <div className="col-12 col-lg-6 px-2 mb-3">
              <label className="p-medium w-100" htmlFor="name">
                {getContactTypeLabel(contactType)}
                <span className="text-danger"> *</span>
              </label>
              <Controller
                name="title"
                control={control}
                rules={{ required: 'Required' }}
                render={({ field }) => (
                  <Dropdown
                    className="input-shadow w-100"
                    placeholder={getContactTypeLabel(contactType)}
                    id={field.name}
                    options={titles}
                    value={field.value}
                    onChange={(e) => {
                      field.onChange(e.value);
                    }}
                    filter
                    disabled={isContactSelected}
                  />
                )}
              />
              {getFormErrorMessage('title')}
            </div>
          )}
          {contactType === 'business' && (
            <div className="col-12 col-lg-6 px-2 mb-3">
              <label className="p-medium w-100" htmlFor="name">
                {getContactTypeLabel(contactType)}
                <span className="text-danger"> *</span>
              </label>
              <Controller
                name="title"
                control={control}
                rules={{ required: 'Required' }}
                render={({ field }) => (
                  <TextSnippetWrapper
                    type="text"
                    disabled={isContactSelected}
                    className="input-shadow w-100"
                    placeholder={getContactTypeLabel(contactType)}
                    {...field}
                  />
                )}
              />
              {getFormErrorMessage('title')}
            </div>
          )}
          {/* first name,middle, last name field */}
          {contactType === 'person' && (
            <>
              <div className="col-12 col-lg-6 px-2 mb-3">
                <label className="p-medium" htmlFor="name">
                  First Name
                  <span className="text-danger"> *</span>
                </label>
                <Controller
                  name="first_name"
                  control={control}
                  rules={{
                    required: 'Required',
                    pattern: {
                      value: /^[aA-zZ\s'-]+$/,
                      message: 'Only letters, apostrophes, hyphens, or underscores are allowed',
                    },
                  }}
                  render={({ field }) => (
                    <TextSnippetWrapper
                      type="text"
                      disabled={isContactSelected}
                      className="input-shadow w-100"
                      placeholder="First Name"
                      {...field}
                    />
                  )}
                />
                {getFormErrorMessage('first_name')}
              </div>
              <div className="col-12 col-lg-6 px-2 mb-3">
                <label className="p-medium" htmlFor="name">
                  Middle Name
                </label>
                <Controller
                  name="middle_name"
                  control={control}
                  rules={{
                    pattern: {
                      value: /^[aA-zZ\s'-]+$/,
                      message: 'Only letters, apostrophes, hyphens, or underscores are allowed',
                    },
                  }}
                  render={({ field }) => (
                    <TextSnippetWrapper
                      type="text"
                      disabled={isContactSelected}
                      className="input-shadow w-100"
                      placeholder="  Middle Name"
                      {...field}
                    />
                  )}
                />
                {getFormErrorMessage('middle_name')}
              </div>
              <div className="col-12 col-lg-6 px-2 mb-3">
                <label className="p-medium" htmlFor="name">
                  Last Name
                  <span className="text-danger"> *</span>
                </label>
                <Controller
                  name="last_name"
                  control={control}
                  rules={{
                    required: 'Required',
                    pattern: {
                      value: /^[aA-zZ\s'-]+$/,
                      message: 'Only letters, apostrophes, hyphens, or underscores are allowed',
                    },
                  }}
                  render={({ field }) => (
                    <TextSnippetWrapper
                      type="text"
                      disabled={isContactSelected}
                      className="input-shadow w-100"
                      placeholder="Last Name"
                      {...field}
                    />
                  )}
                />
                {getFormErrorMessage('last_name')}
              </div>
            </>
          )}
          <div className="col-12 col-lg-6 px-2 mb-3">
            <label className="p-medium" htmlFor="role">
              Email
              {watch('is_create_client_portal') ? <span className="text-danger"> *</span> : null}
            </label>
            <Controller
              name="email"
              control={control}
              rules={{
                required: watch('is_create_client_portal') ? 'Email is required.' : false,
                pattern: {
                  value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                  message: 'Entered value does not match email format',
                },
                validate: { asyncValidation: (v) => emailValidation(v) },
              }}
              render={({ field }) => (
                <InputText
                  disabled={isContactSelected}
                  placeholder="Type in"
                  id={field.name}
                  {...field}
                  // onChange={(e) => setValue('email', e.target.value)}
                  onChange={(e) => {
                    const trimmedValue = e.target.value.trim();
                    setValue('email', trimmedValue);
                  }}
                  onBlur={() => trigger('email')}
                  className="w-100 input-shadow"
                />
              )}
            />
            {getFormErrorMessage('email')}
          </div>

          <div className="col-12 col-lg-6 px-2 mb-3">
            <label className="p-medium" htmlFor="phone">
              Phone Number
            </label>
            <Controller
              name="phone"
              control={control}
              rules={{
                validate: { asyncValidation: (v) => validatePhone(v) },
                minLength: { value: 10, message: 'Min length should be 10' },
              }}
              render={({ field }) => (
                <NumberFormat
                  className="input-shadow w-100 p-2 border-0 number-format p-inputtext"
                  placeholder="Type in"
                  disabled={isContactSelected}
                  format="(###) ###-####"
                  mask="_"
                  name="phone"
                  keyfilter={'pnum'}
                  onValueChange={(e) => setValue('phone', e.value)}
                  value={field.value}
                  onBlur={() => trigger('phone')}
                />
              )}
            />
            {getFormErrorMessage('phone')}
          </div>

          {(contactType === 'person' || contactType === 'business') && (
            <div className="col-12 col-lg-6 px-2 mb-3">
              <label className="p-medium" htmlFor="Business_Role">
                {contactType === 'business' ? 'Business' : null} Role
                <span className="text-danger"> *</span>
              </label>
              <Controller
                name="role"
                control={control}
                rules={{ required: 'Required' }}
                render={({ field: { onChange, value } }) => (
                  <Dropdown
                    value={value}
                    onChange={(e) => onChange(e.value)}
                    name="role"
                    options={contactType === 'business' ? businessRoleList : updatedRelationList}
                    optionLabel={contactType === 'business' ? 'name' : 'relation_role'}
                    optionValue={contactType === 'business' ? 'business_role_id' : 'relation_role_id'}
                    placeholder="Select"
                    className="w-100"
                    inputStyle={{ width: '100%' }}
                    filter
                  />
                )}
              />
              {getFormErrorMessage('role')}
            </div>
          )}

          {/* Insurance Provider Fields - Show when business role is Insurance Provider */}
          {contactType === 'business' &&
            (() => {
              const selectedRole = businessRoleList?.find((r) => r.business_role_id === watch('role'));
              return selectedRole?.name === 'Insurance Provider';
            })() && (
              <div className="col-12">
                <InsuranceProviderFields control={control} watch={watch} setValue={setValue} getFormErrorMessage={getFormErrorMessage} />
              </div>
            )}

          <div className="col-12 col-lg-6 px-2 mb-3">
            <label className="p-medium" htmlFor="name">
              Fax Number
            </label>
            <Controller
              name="fax_number"
              control={control}
              render={({ field }) => (
                <TextSnippetWrapper
                  type="text"
                  disabled={isContactSelected}
                  className="input-shadow w-100"
                  placeholder="Fax Number"
                  {...field}
                />
              )}
            />
          </div>
        </div>
        <div className="col-12 col-lg-6">
          {/* <div className="col-12 mb-3 px-2">
            {(contactType === 'business' && businessType === 'expert') && (
              <div className="col-12 mt-1">
                <label className="p-medium" htmlFor="Area_of_Expertise">
                  {contactType === 'vendor'} Expert Type
                  <span className="text-danger"> *</span>
                </label>
                <Controller
                  name="title"
                  control={control}
                  rules={{ required: 'Required' }}
                  render={({ field: { onChange, value } }) => (
                    <Dropdown
                      value={value}
                      onChange={(e) => onChange(e.value)}
                      name="title"
                      options={expertList}
                      optionLabel="title"
                      optionValue="title"
                      placeholder="Select"
                      className="w-100"
                      inputStyle={{ width: '100%' }}
                      filter
                    />
                  )}
                />
                {getFormErrorMessage('title')}
              </div>
            )}
          </div> */}

          <div className="row checkbox-row px-3 mt-3">
            {!isLeadSpecific && contactType === 'person' && (
              <div className="col-12 col-lg-6 mb-3 px-2">
                <div className="d-flex align-items-center">
                  <Controller
                    name="is_primary_contact"
                    control={control}
                    render={({ field: { value, onChange } }) => (
                      <Checkbox
                        name="is_primary_contact"
                        checked={value}
                        onChange={(e) => onChange(e.checked)}
                        // disabled={isCaseClosed || (isLeadClosed && isLeadSpecific)}
                      />
                    )}
                  />
                  <label className="mx-2 p-medium" htmlFor="is_primary_contact">
                    Primary Contact
                  </label>
                </div>
              </div>
            )}
            {contactType !== 'location' && (
              <div className="col-12 col-lg-6 mb-3 px-2">
                <div className="d-flex align-items-center">
                  <Controller
                    name="is_billing_contact"
                    control={control}
                    render={({ field: { value, onChange } }) => (
                      <Checkbox name="is_billing_contact" checked={value} onChange={(e) => onChange(e.checked)} />
                    )}
                  />
                  <label className="mx-2 p-medium" htmlFor="is_billing_contact">
                    Billable Contact
                  </label>
                </div>
              </div>
            )}
            {contactType === 'location' && (
              <div className="col-12 mb-3 px-2">
                <div className="d-flex align-items-center">
                  <Controller
                    name="is_court_location"
                    control={control}
                    render={({ field: { value, onChange } }) => (
                      <InputSwitch
                        name="is_court_location"
                        checked={value}
                        onChange={(e) => {
                          onChange(e.target.value);
                        }}
                        className="input-swich"
                      />
                    )}
                  />
                  <label className="mx-2 p-medium" htmlFor="is_court_location">
                    Mark as Court Location
                  </label>
                </div>
              </div>
            )}
            {contactType === 'person' ? (
              <>
                <div className="col-12 col-lg-6 mb-3 px-2">
                  <div className="d-flex align-items-center">
                    <Controller
                      name="is_create_client_portal"
                      control={control}
                      render={({ field: { value, onChange } }) => (
                        <Checkbox
                          name="is_create_client_portal"
                          checked={value}
                          onChange={(e) => {
                            onChange(e.checked);
                            if (isContactSelected) {
                              updateContactHandler({ ...selectedContact, is_create_client_portal: e.checked }, isLeadSpecific);
                            }
                          }}
                          disabled={!watch('email')}
                        />
                      )}
                    />
                    <label className="mx-2 p-medium enable_lawft_acess" htmlFor="is_create_client_portal">
                      Enable Lawft Access
                    </label>
                  </div>
                  {watch('email') ? (
                    ''
                  ) : (
                    <Tooltip target=".enable_lawft_acess" position="right" className="case-tooltip">
                      {'Add Email To Enable Lawft Access'}
                    </Tooltip>
                  )}
                </div>

                {/* <div className="col-12 mb-3 px-2">
                  <div className="d-flex align-items-center">
                    <Controller
                      name="notify_case_updates"
                      control={control}
                      render={({ field: { value, onChange } }) => (
                        <Checkbox
                          name="notify_case_updates"
                          checked={value}
                          onChange={(e) => onChange(e.checked)}
                          disabled={!watch('is_create_client_portal')}
                        />
                      )}
                    />
                    <label className="mx-2 p-medium" htmlFor="notify_case_updates">
                      Notify Case Updates
                    </label>
                  </div>
                </div> */}
                {isLeadSpecific && (
                  <div className="col-12 mb-3 px-2">
                    <div className="d-flex align-items-center">
                      <Controller
                        name="type"
                        control={control}
                        render={({ field: { value, onChange } }) => (
                          <Checkbox
                            name="type"
                            checked={value === 'on-behalf'}
                            onChange={(e) => onChange(e.checked ? 'on-behalf' : undefined)}
                          />
                        )}
                      />
                      <label className="mx-2 p-medium" htmlFor="is_billing_contact">
                        On-behalf Contact
                        <GlobalTooltip tooltip={'Person who made first contact'}>
                          <i className="ms-1 pi pi-info-circle on-behalf-infos cursor-pointer" style={{ fontSize: '13px' }} />
                        </GlobalTooltip>
                      </label>
                    </div>
                  </div>
                )}
              </>
            ) : null}
            {contactType !== 'location' && (
              <div className="col-12 mb-3 px-2">
                <div className="d-flex align-items-center">
                  <Controller
                    name="is_client_opt_in"
                    control={control}
                    defaultValue={true}
                    render={({ field }) => (
                      <Checkbox name="is_create_client_portal" checked={field.value} onChange={(e) => field.onChange(e.checked)} />
                    )}
                  />
                  <label className="mx-2 p-medium" htmlFor="notify_case_updates">
                    {contactTextMessageLabel}
                  </label>
                </div>
              </div>
            )}
          </div>

          <div className="col-12 px-2 mb-1">
            {checkSubPermission('custom_fields') && !isContactSelected && contactType !== 'location' && (
              <>
                <div className="d-flex align-items-center py-2">
                  <div className="col-sm-6 d-flex addfield">
                    <Controller
                      name="custom_field_toggle"
                      control={control}
                      defaultValue={false}
                      render={({ field }) => (
                        <Button
                          id={field.name}
                          type="button"
                          className="p-button button-text text-medium addfield ps-0"
                          onClick={(e) => field.onChange(!field.value)}
                        >
                          <i className={field?.value ? 'pi pi-minus F-size14 me-1' : 'pi pi-plus F-size14 me-1'}></i>Add Custom Fields
                        </Button>
                      )}
                    />
                  </div>
                </div>
                {watch('custom_field_toggle') && <CustomFields field="custom_field" control={control} />}
              </>
            )}
          </div>
        </div>
      </div>
      <div className="col-12 col-lg-12 px-2 mb-2">
        <div className="footer-btn">
          <Button
            onClick={
              toggle
                ? toggle === 'person'
                  ? toggleAddContact
                  : toggle === 'business'
                  ? toggleAddBusiness
                  : toggleAddLocation
                : toggleAddContact
            }
            className="p-button p-button-secondary outline me-2"
          >
            Cancel
          </Button>
          <Button
            className="p-button p-button-secondary"
            onClick={handleSubmit((formdata) => onContactSubmit(formdata, !isContactSelected, toggle))}
          >
            Add
          </Button>
        </div>
      </div>
    </React.Fragment>
  );
}
